import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { translate, useTranslate } from 'hooks/useTranslate';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { differenceInDays } from 'date-fns';

import menuTypes from 'constants/menuTypes';
import sizes from 'constants/sizes';

import { Partner, Society } from 'types';
import { setModal } from 'modules/_shared/redux/modalActions';
import { getPartner, getPartners } from 'modules/partners/redux/partnerActions';
import { store } from 'redux/store';
import { TradingOfferDetailContextType } from '../types/TradingOfferDetailContextType';
import { tradingOfferDetailInitValue } from '../constants/TradingOfferDetailInitValue';
import { getDefaultTimelineSteps } from '../constants/tradingOfferTimelineSteps';
import {
  TradingOffer,
  TradingOfferStatus,
  TradingOfferPriceContext,
  TradingOfferTimelineStep,
  TradingOfferSellerInfo,
  PartnerAgreedTradingOffer,
} from '../../../../../types/TradingOffer';
import { TradingOfferOperation } from '../../../../../types/TradingOfferOperation';
import { getTradingOfferOperations } from '../../../redux/tradingOfferOperationActions';
import { getTradingOffer } from '../../../redux/tradingOfferActions';
import AcceptTradingOfferModal from '../../../modals/AcceptTradingOffer';
import { AcceptTradingOfferModalProvider } from '../../../modals/AcceptTradingOffer/context/AcceptTradingOfferModalContext';
import getOfferReference from '../../../utils/getOfferReference';
import { getOfferStatus } from '../../../utils/getOfferStatus';

const COMMISSION_RATE = 0.015;

const getPurchaseQuantityBounds = (
  currentOffer: TradingOffer | null,
  availableSharesValue: number
): { min: number; max: number } => {
  const max = Math.max(0, availableSharesValue);
  if (!currentOffer) {
    return { min: max > 0 ? 1 : 0, max };
  }
  if (!currentOffer.isPartialAmountEnabled) {
    return { min: max, max };
  }
  const rawMin = currentOffer.minSharesPerAcceptance ?? 1;
  if (max === 0) {
    return { min: 0, max: 0 };
  }
  const min = Math.max(1, Math.min(rawMin, max));
  return { min, max };
};

export const TradingOfferDetailContext =
  createContext<TradingOfferDetailContextType>(tradingOfferDetailInitValue);

export const TradingOfferDetailProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId, tradingOfferId } = useParams<{
    societyId: string;
    tradingOfferId: string;
  }>();
  const { t } = useTranslate();
  const history = useHistory();
  const dispatch = useDispatch();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const partnerId = queryParams.get('partnerId');
  const partnerQueryParam = partnerId ? `?partnerId=${partnerId}` : '';

  const actualSociety: Society = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );

  const [userPartnerIds, setUserPartnerIds] = useState<string[]>([]);

  const [partnerBuyer, setPartnerBuyer] = useState<Partner | null>(null);

  const [offer, setOffer] = useState<TradingOffer | null>(null);
  const [acceptanceHistory, setAcceptanceHistory] = useState<
    PartnerAgreedTradingOffer[]
  >([]);
  const [priceContext, setPriceContext] = useState<TradingOfferPriceContext>({
    currentOfferPrice: 0,
    lastTransactionPrice: 0,
    avgSellOffersPrice: 0,
    officialValuation: 0,
    userAvgBuyPrice: 0,
  });
  const [sellerInfo, setSellerInfo] = useState<TradingOfferSellerInfo | null>(
    null
  );
  const [tradingOfferOperations, setTradingOfferOperations] = useState<
    TradingOfferOperation[]
  >([]);
  const [purchaseQuantity, setPurchaseQuantity] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const offerStatus = useMemo((): TradingOfferStatus => {
    if (!offer) return TradingOfferStatus.ACTIVE;
    return getOfferStatus(offer);
  }, [offer]);

  const isExpired = offerStatus === TradingOfferStatus.EXPIRED;

  const isOwnOffer = useMemo(() => {
    if (!offer) return false;
    const publisherId =
      typeof offer.partner === 'string' ? offer.partner : offer.partner?._id;
    const publisherIdStr = publisherId ? String(publisherId) : '';

    if (partnerId && publisherIdStr === partnerId) {
      return true;
    }

    return Boolean(publisherIdStr && userPartnerIds.includes(publisherIdStr));
  }, [offer, partnerId, userPartnerIds]);

  const offerReference = useMemo(() => getOfferReference(offer), [offer]);

  const availableShares = useMemo(() => {
    if (!offer) return 0;
    return offer.sharesCount.total - offer.sharesCount.agreed;
  }, [offer]);

  const daysRemaining = useMemo(() => {
    if (!offer) return 0;
    const now = new Date();
    const endDate = new Date(offer.endDate);
    const days = differenceInDays(endDate, now);
    return Math.max(0, days);
  }, [offer]);

  const timelineSteps = useMemo((): TradingOfferTimelineStep[] => {
    if (!offer) return getDefaultTimelineSteps();

    const steps = getDefaultTimelineSteps();
    const agreedCount = offer.sharesCount.agreed;
    const approvedCount = offer.sharesCount.approved;
    const totalCount = offer.sharesCount.total;

    // Step 1: Published - always completed if offer exists
    steps[0].isCompleted = true;

    // Step 2: Waiting acceptance - completed if any shares agreed
    steps[1].isCompleted = agreedCount > 0;
    steps[1].isCurrent = agreedCount === 0;

    // Step 3: KYC verification - completed if shares approved
    steps[2].isCompleted = approvedCount > 0;
    steps[2].isCurrent = agreedCount > 0 && approvedCount === 0;

    // Step 4: Society validation - completed if all approved
    steps[3].isCompleted = approvedCount === totalCount && totalCount > 0;
    steps[3].isCurrent = approvedCount > 0 && approvedCount < totalCount;

    // Step 5: Transfer - completed if fully accepted
    steps[4].isCompleted = offerStatus === TradingOfferStatus.ACCEPTED;
    steps[4].isCurrent =
      approvedCount === totalCount &&
      offerStatus !== TradingOfferStatus.ACCEPTED;

    return steps;
  }, [offer, offerStatus]);

  const handleGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handlePurchaseQuantityChange = useCallback(
    (quantity: number) => {
      const { min, max } = getPurchaseQuantityBounds(offer, availableShares);

      if (max <= 0) {
        setPurchaseQuantity(0);
        return;
      }

      const clampedQuantity = Math.max(min, Math.min(quantity, max));
      setPurchaseQuantity(clampedQuantity);
    },
    [availableShares, offer]
  );

  useEffect(() => {
    const { min, max } = getPurchaseQuantityBounds(offer, availableShares);

    setPurchaseQuantity((prev) => {
      if (max <= 0) return 0;
      if (offer && !offer.isPartialAmountEnabled) {
        return max;
      }
      if (prev === 0) return 0;
      return Math.max(min, Math.min(prev, max));
    });
  }, [offer, availableShares]);

  const handleBuySharesSuccess = useCallback(() => {
    setOffer((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sharesCount: {
          ...prev.sharesCount,
          agreed: prev.sharesCount.agreed + purchaseQuantity,
        },
      };
    });
  }, [purchaseQuantity]);

  const handleBuyShares = useCallback(() => {
    if (!societyId || !offer || !user || !partnerBuyer) return;

    const realSellerName =
      typeof offer.partner === 'string'
        ? offer.partner
        : offer.partner?.name || '';

    const sellerName = offer.isSellerNameHidden
      ? t('HiddenName')
      : realSellerName;

    const shareClassName =
      typeof offer.shareClass === 'string' ? '' : offer.shareClass?.name || '';

    dispatch(
      setModal(
        <AcceptTradingOfferModalProvider
          offer={offer}
          partnerBuyer={partnerBuyer}
          purchaseQuantity={purchaseQuantity}
          societyId={societyId}
          offerReference={offerReference}
          sellerName={sellerName}
          className={shareClassName}
          onSuccess={handleBuySharesSuccess}
          size={sizes.LG}
        >
          <AcceptTradingOfferModal />
        </AcceptTradingOfferModalProvider>
      )
    );
  }, [
    offer,
    user,
    partnerBuyer,
    societyId,
    purchaseQuantity,
    offerReference,
    dispatch,
    handleBuySharesSuccess,
    t,
  ]);

  const handleViewOtherOffers = useCallback(() => {
    history.push(`/ofertas/${societyId}${partnerQueryParam}`);
  }, [history, societyId, partnerQueryParam]);

  const handleGoToOperation = useCallback(
    (operationId: string) => {
      history.push(
        `/detalle-operacion-oferta/${societyId}/${operationId}${partnerQueryParam}`
      );
    },
    [history, societyId, partnerQueryParam]
  );

  useEffect(() => {
    if (!partnerId) return;

    const fetchPartner = async () => {
      const partner: Partner | null = await store.dispatch(
        getPartner(partnerId)
      );
      if (!partner) {
        return;
      }
      setPartnerBuyer(partner);
    };

    fetchPartner();
  }, [partnerId, dispatch]);

  useEffect(() => {
    if (!societyId || !user?._id) {
      setUserPartnerIds([]);
      return;
    }

    let cancelled = false;

    const fetchUserPartners = async () => {
      const res = await store.dispatch(
        getPartners(societyId, { userId: user._id, items: 100 })
      );
      if (cancelled) {
        return;
      }
      const ids =
        res?.data?.map((p: Partner) => String(p._id)).filter(Boolean) ?? [];
      setUserPartnerIds(ids);
    };

    fetchUserPartners();

    return () => {
      cancelled = true;
    };
  }, [societyId, user?._id]);

  const actualSocietyId = actualSociety?._id;

  useEffect(() => {
    if (!societyId || !tradingOfferId || !actualSocietyId) {
      return undefined;
    }

    let cancelled = false;

    const fetchOfferDetail = async () => {
      setIsLoading(true);

      try {
        let currentOffer: TradingOffer | null = null;
        try {
          currentOffer = await store.dispatch(getTradingOffer(tradingOfferId));
        } catch {
          currentOffer = null;
        }

        if (cancelled) {
          return;
        }

        if (
          currentOffer &&
          String(currentOffer.society) !== String(actualSocietyId)
        ) {
          setOffer(null);
          setAcceptanceHistory([]);
          setTradingOfferOperations([]);
          setSellerInfo(null);
          return;
        }

        setOffer(currentOffer);
        setAcceptanceHistory([]);

        const operationsResponse = await store.dispatch(
          getTradingOfferOperations({ tradingOfferId })
        );

        if (cancelled) {
          return;
        }

        setTradingOfferOperations(operationsResponse?.data || []);
        setPriceContext({
          currentOfferPrice: currentOffer?.sharePrice || 0,
          lastTransactionPrice: 0,
          avgSellOffersPrice: 0,
          officialValuation: 0,
          userAvgBuyPrice: 0,
        });

        const sellerPartnerId =
          typeof currentOffer?.partner === 'string'
            ? currentOffer.partner
            : currentOffer?.partner?._id;

        if (sellerPartnerId) {
          const sellerPartner: Partner | null = await store.dispatch(
            getPartner(sellerPartnerId)
          );

          if (cancelled) {
            return;
          }

          if (sellerPartner) {
            const partnerShares = sellerPartner.sharesCount?.actual || 0;
            const totalSocietyShares = actualSociety?.sharesCount?.actual || 1;

            const isHidden = currentOffer?.isSellerNameHidden === true;

            let memberSince = '';
            if (!isHidden && sellerPartner.registerDate) {
              memberSince = new Date(sellerPartner.registerDate)
                .getFullYear()
                .toString();
            }

            setSellerInfo({
              partnerId: sellerPartner._id,
              name: isHidden ? translate('HiddenName') : sellerPartner.name || '',
              image: isHidden ? '' : sellerPartner.image || '',
              memberSince,
              totalShares: partnerShares,
              capitalPercentage:
                totalSocietyShares > 0
                  ? (partnerShares / totalSocietyShares) * 100
                  : 0,
              previousOperations: 0,
              rating: 0,
            });
          } else {
            setSellerInfo(null);
          }
        } else {
          setSellerInfo(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching trading offer detail:', error);
          setOffer(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchOfferDetail();

    return () => {
      cancelled = true;
    };
  }, [societyId, tradingOfferId, actualSocietyId]);

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        offer,
        offerStatus,
        offerReference,
        acceptanceHistory,
        tradingOfferOperations,
        priceContext,
        timelineSteps,
        sellerInfo,
        availableShares,
        daysRemaining,
        isAdmin,
        isExpired,
        isOwnOffer,
        userPartnerIds,
        partnerQueryParam,
        isLoading: isLoading || !actualSociety,
        hasPartnerBuyer: !!partnerBuyer,
        purchaseQuantity,
        commissionRate: COMMISSION_RATE,
      },
      actions: {
        handleGoBack,
        handlePurchaseQuantityChange,
        handleBuyShares,
        handleViewOtherOffers,
        handleGoToOperation,
      },
    }),
    [
      societyId,
      actualSociety,
      offer,
      offerStatus,
      offerReference,
      acceptanceHistory,
      tradingOfferOperations,
      priceContext,
      timelineSteps,
      sellerInfo,
      availableShares,
      daysRemaining,
      isAdmin,
      isExpired,
      isOwnOffer,
      userPartnerIds,
      partnerQueryParam,
      isLoading,
      partnerBuyer,
      purchaseQuantity,
      handleGoBack,
      handlePurchaseQuantityChange,
      handleBuyShares,
      handleViewOtherOffers,
      handleGoToOperation,
    ]
  );

  return (
    <TradingOfferDetailContext.Provider value={contextValue}>
      {children}
    </TradingOfferDetailContext.Provider>
  );
};

export const useTradingOfferDetail = () => {
  const context = useContext(TradingOfferDetailContext);
  if (!context) {
    throw new Error(
      'useTradingOfferDetail must be used within a TradingOfferDetailProvider'
    );
  }
  return context;
};
